import { DocumentMagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useVerification } from "src/contexts/VerificationContext";
import { useEffect, useState, useCallback, useMemo } from "react";
import BlurredCard from "../BlurredCard";
import { LabResult } from "src/shared/types";
import Loading from "../Loading";
import { capitalizeStatus } from "src/utils/utils";
import { apiGet } from "src/utils/api";
import { StaticDataTable, StaticDataColumnProps, StaticDataRow } from "../table/StaticDataTable";
import { SortDirection } from "../table/useTableData";
import { useCurrentOrganization, useOrganizationContext } from "src/contexts/OrganizationContext";
import TableDateTime, { getDateTimeSortValue } from "../table/TableDateTime";
import TableBadge from "../table/TableBadge";

const statusColorMap: Record<string, string> = {
  final: "bg-emerald-500",
  preliminary: "bg-amber-500",
  amended: "bg-sky-500",
  cancelled: "bg-rose-500",
};

const labResultColumns: StaticDataColumnProps[] = [
  { title: "Name", sortable: true },
  { title: "Date & Time", sortable: true, defaultSort: true, defaultSortDirection: SortDirection.DESC },
  { title: "Value", sortable: false },
  { title: "Reference", sortable: false },
  { title: "Status", sortable: true },
  { title: "Result", sortable: true },
];

const convertLabResultsToRows = (labResults: LabResult[]): StaticDataRow[][] => {
  return labResults.map((labResult) => [
    {
      name: "Name",
      value: labResult.name,
      element: <span className="text-gray-900">{labResult.name}</span>
    },
    {
      name: "Date & Time",
      value: getDateTimeSortValue(labResult.date, labResult.time),
      element: <TableDateTime date={labResult.date} startTime={labResult.time} />
    },
    {
      name: "Value",
      value: labResult.value,
      element: (
        <span className={`tabular-nums ${labResult.isAbnormal ? 'text-rose-600' : 'text-gray-900'}`}>
          {labResult.value} <span className="text-gray-900 text-xs">{labResult.unit}</span>
        </span>
      )
    },
    {
      name: "Reference",
      value: labResult.referenceRange || "—",
      element: <span className="text-gray-900 text-sm">{labResult.referenceRange || "—"}</span>
    },
    {
      name: "Status",
      value: labResult.status,
      element: (
        <TableBadge
          text={capitalizeStatus(labResult.status)}
          bgColorClass={statusColorMap[labResult.status] ?? "bg-gray-500"}
        />
      )
    },
    {
      name: "Result",
      value: labResult.isAbnormal ? 1 : 0,
      element: (
        <TableBadge
          text={labResult.isAbnormal ? "Abnormal" : "Normal"}
          bgColorClass={labResult.isAbnormal ? "bg-rose-500" : "bg-emerald-500"}
        />
      )
    }
  ]);
};

const dummyLabResults: LabResult[] = [
  {
    name: "Lab Result 1",
    value: "100",
    unit: "%",
    date: new Date().toISOString().split('T')[0],
    time: "10:00",
    status: "final",
    isAbnormal: true,
    referenceRange: "100-120 bpm",
    labCategory: "Laboratory",
    note: "Note 1",
  },
  {
    name: "Lab Result 2",
    value: "100",
    unit: "bpm",
    date: new Date().toISOString().split('T')[0],
    time: "10:00",
    status: "final",
    isAbnormal: false,
    referenceRange: "100-120 bpm",
    labCategory: "Laboratory",
    note: "Note 2",
  },
];

type LabResultCardProps = {
  yearFrom: string;
  yearTo: string;
  refreshKey?: number;
};

const LabResultCard = ({ yearFrom, yearTo, refreshKey }: LabResultCardProps) => {
  const organization = useCurrentOrganization();
  const { patientId } = useOrganizationContext();
  const [ labResults, setLabResults ] = useState<LabResult[]>([]);
  const [ loading, setLoading ] = useState(false);

  const { isVerified } = useVerification();
  const showLoading = isVerified && loading;

  const fetchLabResults = useCallback(async () => {
    if (!organization || !patientId) {
      setLabResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const params = { yearFrom, yearTo };
      const response = await apiGet(`/api/${organization.id}/patients/${patientId}/lab-results`, params);
      const data = await response.json();

      if (data.error) {
        setLabResults([]);
        window.location.href = "/error";
      }
      setLabResults(data.labResults);
    } catch (_) {
      setLabResults([]);
    } finally {
      setLoading(false);
    }
  }, [ organization, patientId, yearFrom, yearTo ]);

  useEffect(() => {
    if (isVerified) {
      fetchLabResults();
    } else {
      setLabResults(dummyLabResults);
    }
  }, [ isVerified, fetchLabResults, yearFrom, yearTo, refreshKey ]);

  const tableRows = useMemo(() =>
    convertLabResultsToRows(labResults),
    [ labResults ]
  );

  return (
    <BlurredCard>
      {showLoading ? (
        <div className="flex justify-center mt-8">
          <Loading message="Loading Lab Results..." borderColor="border-brand-500" />
        </div>
      ) : (
        <div className="-mx-2">
          {labResults.length > 0 ? (
            <StaticDataTable
              tableId="lab-results-table"
              columns={labResultColumns}
              rows={tableRows}
              updateUrlParams={false}
              rowId={(item) => `${item.Name}-${item.Value}`}
            />
          ) : (
            <div className="text-center py-12 text-gray-400">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <DocumentMagnifyingGlassIcon className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-medium">No lab results found</p>
            </div>
          )}
        </div>
      )}
    </BlurredCard>
  );
};

export default LabResultCard;
